﻿// #region Imports
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { AvatarSize, DEFAULT_AVATAR_SIZE, getAvatarCacheKey } from "marketplace/_views/avatars/avatar-size";
import { marketplaceTeamsApi } from "marketplace/teams/_api/marketplace-team-api";
import { TeamJSON } from "marketplace/teams/_schema/team-schema";
import { TeamInvitationJSON } from "marketplace/teams/invitations/_schemas/team-invitation/team-invitation-schema";
import TeamInvitation from "marketplace/teams/invitations/team-invitation";
import Team from "marketplace/teams/team";
// #endregion

// #region Store State Type
export type MarketplaceTeamStoreState = {
  vendors: {
    entries?: TeamJSON[];
  };
  byId: {
    entries: Record<string, TeamJSON>;
  };
  avatars: {
    entries: Record<string, string>;
  };
  invitations: {
    byTeamId: {
      entries: Record<string, TeamInvitationJSON[]>;
    };
    byId: {
      entries: Record<string, TeamInvitationJSON>;
    };
  };
};
// #region Initial State
const initialState: MarketplaceTeamStoreState = {
  vendors: {
    entries: undefined,
  },
  byId: {
    entries: {},
  },
  avatars: {
    entries: {},
  },
  invitations: {
    byTeamId: {
      entries: {},
    },
    byId: {
      entries: {},
    },
  },
};

// #region Slice
export const marketplaceTeamsSlice = createSlice({
  name: "marketplaceTeams",
  initialState,
  // #region Simple Reducers
  reducers: {
    addMarketplaceTeam: (state, action) => {
      const teamId = action.payload.id?.value;
      if (!teamId) return;
      state.byId.entries[teamId] = action.payload;
      state.vendors.entries = state.vendors.entries?.filter((team) => team.id !== teamId) ?? [];
      state.vendors.entries.push(action.payload);
    },
    addMarketplaceTeamJSON: (state, action: { payload: TeamJSON }) => {
      const teamJSON = action.payload;
      if (!teamJSON.id) return;

      state.byId.entries[teamJSON.id] = teamJSON;
      state.vendors.entries = state.vendors.entries?.filter((team) => team.id !== teamJSON.id) ?? [];
      state.vendors.entries.push(teamJSON);
    },
    removeMarketplaceTeam: (state, action) => {
      delete state.byId.entries[action.payload.value];
    },
    addAvatar: (state, action) => {
      state.avatars.entries[action.payload.teamId.value] = action.payload.avatar;
      state.avatars.entries[getAvatarCacheKey(action.payload.teamId.value)] = action.payload.avatar;
    },
    removeAvatar: (state, action) => {
      delete state.avatars.entries[action.payload.value];
      delete state.avatars.entries[getAvatarCacheKey(action.payload.value, "small")];
      delete state.avatars.entries[getAvatarCacheKey(action.payload.value, "medium")];
      delete state.avatars.entries[getAvatarCacheKey(action.payload.value, "large")];
    },
    addMarketplaceTeamInvitation: (state, action) => {
      const invitationId = action.payload.id?.value;
      if (!invitationId) return;
      state.invitations.byId.entries[invitationId] = action.payload;
    },
    removeMarketplaceTeamInvitation: (state, action) => {
      const invitationId = action.payload.value;
      if (!invitationId) return;
      delete state.invitations.byId.entries[invitationId];
      for (const teamId in state.invitations.byTeamId.entries) {
        state.invitations.byTeamId.entries[teamId] = state.invitations.byTeamId.entries[teamId].filter(
          (invitation) => invitation.id !== invitationId,
        );
      }
    },
  },
  // #region Async Reducers
  extraReducers: (builder) => {
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamVendors.matchFulfilled, (state, action) => {
      state.vendors.entries = action.payload;
    });
    // #endregion

    // #region getTeamById
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamById.matchFulfilled, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      if (!action.payload) {
        console.error("MarketplaceTeam not received by store.");
        return;
      }
      state.byId.entries[teamId] = action.payload;
    });
    // #endregion

    // #region getTeamsByIds
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamsByIds.matchFulfilled, (state, action) => {
      for (const team of action.payload) {
        state.byId.entries[team.id] = team;
      }
    });
    // #endregion

    // #region getTeamAvatar
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamAvatar.matchFulfilled, (state, action) => {
      const { teamId, size = DEFAULT_AVATAR_SIZE } = action.meta.arg.originalArgs;
      state.avatars.entries[getAvatarCacheKey(teamId, size)] = action.payload;
      if(size === DEFAULT_AVATAR_SIZE) state.avatars.entries[teamId] = action.payload;
    });
    // #endregion

    // #region getTeamInvitationByInvitationId
    builder.addMatcher(
      marketplaceTeamsApi.endpoints.getTeamInvitationByInvitationId.matchFulfilled,
      (state, action) => {
        const invitationId = action.meta.arg.originalArgs.invitationId;
        state.invitations.byId.entries[invitationId] = action.payload;
      },
    );
    // #endregion

    // #region getTeamInvitationsByTeamId
    builder.addMatcher(marketplaceTeamsApi.endpoints.getTeamInvitationsByTeamId.matchFulfilled, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.invitations.byTeamId.entries[teamId] = action.payload;
    });
    // #endregion

    // #region createTeam
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeam.matchFulfilled, (state, action) => {
      const teamId = action.payload.id;

      if (!teamId) return;

      state.byId.entries[teamId] = action.payload;
      if (!state.vendors.entries) {
        state.vendors.entries = [];
      }
      state.vendors.entries.push(action.payload);
    });
    // #endregion

    // #region updateTeam
    builder.addMatcher(marketplaceTeamsApi.endpoints.updateTeam.matchPending, (state, action) => {
      const updatedTeamJson = action.meta.arg.originalArgs.updatedTeam.toJSON();
      const teamId = updatedTeamJson.id;

      if (!teamId) return;

      state.byId.entries[teamId] = updatedTeamJson;

      const vendorIndex = state.vendors.entries?.findIndex((team) => team.id === teamId) ?? -1;
      if (state.vendors.entries && vendorIndex !== -1) {
        state.vendors.entries[vendorIndex] = updatedTeamJson;
      } else {
        state.vendors.entries = state.vendors.entries || [];
        state.vendors.entries.push(updatedTeamJson);
      }
    });
    builder.addMatcher(marketplaceTeamsApi.endpoints.updateTeam.matchFulfilled, (state, action) => {
      const teamId = action.payload.id;

      if (!teamId) return;

      state.byId.entries[teamId] = action.payload;

      const vendorIndex = state.vendors.entries?.findIndex((team) => team.id === teamId) ?? -1;
      if (state.vendors.entries && vendorIndex !== -1) {
        state.vendors.entries[vendorIndex] = action.payload;
      } else {
        state.vendors.entries = state.vendors.entries || [];
        state.vendors.entries.push(action.payload);
      }
    });
    // #endregion

    // #region deleteTeam
    builder.addMatcher(marketplaceTeamsApi.endpoints.deleteTeam.matchFulfilled, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;

      delete state.byId.entries[teamId];

      const targetIndex = state.vendors.entries?.findIndex((team) => team.id === teamId) ?? -1;
      if (targetIndex !== -1 && state.vendors.entries) {
        state.vendors.entries.splice(targetIndex, 1);
      }
    });
    // #endregion

    // #region removeTeamMember
    builder.addMatcher(marketplaceTeamsApi.endpoints.removeTeamMember.matchFulfilled, (state, action) => {
      const { teamId, memberIndividualId } = action.meta.arg.originalArgs;
      const team = state.byId.entries[teamId];

      if (team) {
        team.members = team.members.filter((member) => member.id !== memberIndividualId);
        state.byId.entries[teamId] = team;
      }

      const vendorIndex = state.vendors.entries?.findIndex((t) => t.id === teamId) ?? -1;
      if (state.vendors.entries && vendorIndex !== -1 && team) {
        state.vendors.entries[vendorIndex] = team;
      }
    });
    // #endregion

    // #region createTeamAvatar
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeamAvatar.matchFulfilled, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      if (action.payload) {
        state.avatars.entries[teamId] = action.payload;
        state.avatars.entries[getAvatarCacheKey(teamId, "small")] = action.payload;
        state.avatars.entries[getAvatarCacheKey(teamId, "medium")] = action.payload;
        return;
      }

      delete state.avatars.entries[teamId];
      delete state.avatars.entries[getAvatarCacheKey(teamId, "small")];
      delete state.avatars.entries[getAvatarCacheKey(teamId, "medium")];
      delete state.avatars.entries[getAvatarCacheKey(teamId, "large")];
    });
    // #endregion

    // #region createTeamInvitation
    builder.addMatcher(marketplaceTeamsApi.endpoints.createTeamInvitation.matchFulfilled, (state, action) => {
      const teamId = action.payload.teamId;

      if (!state.invitations.byTeamId.entries[teamId]) {
        state.invitations.byTeamId.entries[teamId] = [];
      }

      state.invitations.byId.entries[action.payload.id] = action.payload;
      state.invitations.byTeamId.entries[teamId].push(action.payload);
    });
    // #endregion

    // #region cancelTeamInvitation
    builder.addMatcher(marketplaceTeamsApi.endpoints.cancelTeamInvitation.matchFulfilled, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.invitationId;

      for (const teamId in state.invitations.byTeamId.entries) {
        state.invitations.byTeamId.entries[teamId] = state.invitations.byTeamId.entries[teamId].filter(
          (invitation) => invitation.id !== invitationId,
        );
      }

      for (const id in state.invitations.byId.entries) {
        if (id === invitationId) {
          delete state.invitations.byId.entries[id];
        }
      }
    });
    // #endregion

    // #region promoteTeamMemberToLeader
    builder.addMatcher(marketplaceTeamsApi.endpoints.promoteTeamMemberToLeader.matchFulfilled, () => {
      // Team data is invalidated; re-fetch via getTeamById will update state
    });
    // #endregion
  },
});

// #region Exports

// #region Team By Id

// getTeamById ----------------------------------------------------------------
const selectTeamById = createSelector(
  [
    (state: RootState) => state.teams.byId.entries,
    (_: RootState, id?: Guid) => id,
  ],
  (entries, id) => {
    if (!id) return undefined;
    const teamJson = entries[id.value];
    if (!teamJson) return undefined;
    return Team.fromJSON(teamJson);
  },
);
export const getTeamById = (id?: Guid) => (state: RootState) => selectTeamById(state, id);
// ----------------------------------------------------------------------------

// #endregion

// #region Teams By Ids

// getTeamsByIds ----------------------------------------------------------
const selectTeamsByIds = createSelector(
  [
    (state: RootState) => state.teams.byId.entries,
    (_: RootState, ids: Guid[]) => ids,
  ],
  (entries, ids) =>
    ids
      .map((id) => entries[id.value])
      .filter((x): x is TeamJSON => !!x)
      .map((json) => Team.fromJSON(json)),
);
export const getTeamsByIds = (ids: Guid[]) => (state: RootState) => selectTeamsByIds(state, ids);
// ----------------------------------------------------------------------------

// #endregion

// #region Team Vendors

// getTeamVendors ----------------------------------------------------------
const selectTeamVendors = createSelector([(state: RootState) => state.teams.vendors.entries], (entries) =>
  !entries ? undefined : entries.map((json) => Team.fromJSON(json)).filter((c): c is Team => !!c),
);
export const getTeamVendors = () => (state: RootState) => selectTeamVendors(state);
// ----------------------------------------------------------------------------

// #endregion

// #region Team Avatar

export const getTeamAvatar = (teamId: Guid | null, size: AvatarSize = DEFAULT_AVATAR_SIZE) => (state: RootState) =>
  teamId
    ? state.teams.avatars.entries[getAvatarCacheKey(teamId.value, size)] ??
      (size === DEFAULT_AVATAR_SIZE ? state.teams.avatars.entries[teamId.value] : undefined)
    : undefined;

// #endregion

// #region Team Invitation by Id

// getTeamInvitationById ------------------------------------------------------
const selectTeamInvitationById = createSelector(
  [
    (state: RootState) => state.teams.invitations.byId.entries,
    (_: RootState, id: Guid) => id,
  ],
  (entries, id) => {
    const invitationJson = entries[id.value];
    if (!invitationJson) return undefined;
    return TeamInvitation.fromJSON(invitationJson);
  },
);
export const getTeamInvitationById = (id: Guid) => (state: RootState) => selectTeamInvitationById(state, id);
// ----------------------------------------------------------------------------

// #endregion

// #region Team Invitation by TeamId

// getTeamInvitationsByTeamId -------------------------------------------------
const selectTeamInvitationsByTeamId = createSelector(
  [
    (state: RootState) => state.teams.invitations.byTeamId.entries,
    (_: RootState, teamId: Guid) => teamId,
  ],
  (entries, teamId) => {
    const invitationsJson = entries[teamId.value];
    if (!invitationsJson) return undefined;
    return invitationsJson.map((json) => TeamInvitation.fromJSON(json));
  },
);
export const getTeamInvitationsByTeamId = (teamId?: Guid) => (state: RootState) =>
  !teamId ? undefined : selectTeamInvitationsByTeamId(state, teamId);
// ----------------------------------------------------------------------------
// #endregion

export const { addMarketplaceTeam, addMarketplaceTeamJSON, removeMarketplaceTeam } = marketplaceTeamsSlice.actions;

// Backwards-compatible helper to populate a team by id.
// Accepts either a Guid or a plain string and returns the RTK Query initiate thunk.
export const populateMarketplaceTeamById = ({
  teamId
}: {
  teamId: Guid | string;
}) => {
  const id = typeof teamId === "string" ? teamId : teamId?.value;
  return marketplaceTeamsApi.endpoints.getTeamById.initiate({ teamId: id });
};
